import { isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Coding,
  Location,
  Organization,
  Practitioner,
  PractitionerRole,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import {
  type CodingValue,
  codeableConcept,
  codeFromCodeableConcept,
  getCodingFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import { PRACTITIONER_ROLE } from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, mergeSystemValues } from '#src/utils.ts'

export class ExtendedPractitionerRole extends ExtendedResource<PractitionerRole> {
  static readonly RESOURCE_TYPE = 'PractitionerRole'

  static isInstance(value: unknown): value is PractitionerRole {
    return isResource(value) && isOfType(value, 'PractitionerRole')
  }

  constructor(data?: Omit<PractitionerRole, 'resourceType'>) {
    invariant(data?.practitioner, 'Practitioner reference for role is required')
    super({ ...data })
  }

  // Active roles for one practitioner, newest first — the shape callers
  // hand-rolled before this helper (e.g. the practitioner hover card).
  static byPractitioner(practitioner: Reference<Practitioner>): QueryTypes {
    return {
      practitioner: practitioner.reference,
      active: true,
      _sort: '-_lastUpdated',
    }
  }

  // Bulk read of active roles across many practitioners in one request.
  // Chunk the input to ~100 refs per call to bound URL length.
  static byPractitioners(practitioners: Reference<Practitioner>[]): QueryTypes {
    return {
      practitioner: compact(practitioners.map((p) => p.reference)).join(','),
      active: true,
      _count: '1000',
    }
  }

  // Active roles for one organization — the membership set a caller
  // intersects against another practitioner list to org-scope it.
  static byOrganization(organization: Reference<Organization>): QueryTypes {
    return {
      organization: organization.reference,
      active: true,
      _count: '1000',
    }
  }

  static build(params: {
    practitioner: Reference<Practitioner>
    roles?: Coding[]
    organization?: Reference<Organization>
    location?: Reference<Location>[]
    specialty?: CodeableConcept[]
  }): ExtendedPractitionerRole {
    const role = new ExtendedPractitionerRole({
      practitioner: params.practitioner,
    })
    if (params.roles) {
      role.roles = params.roles
    }
    if (params.organization) {
      role.organization = params.organization
    }
    if (params.location) {
      role.location = params.location
    }
    if (params.specialty) {
      role.specialty = params.specialty
    }
    return role
  }

  get practitioner(): Reference<Practitioner> {
    invariant(
      this.resource.practitioner,
      'Practitioner reference for role is required'
    )
    return this.resource.practitioner
  }

  set practitioner(practitioner: Reference<Practitioner>) {
    this.resource.practitioner = practitioner
  }

  get roles(): Coding[] {
    return (
      this.resource.code
        ?.map((code) => getCodingFromCodeableConcept(code))
        .filter((c): c is Coding => !!c) ?? []
    )
  }

  set roles(codings: Coding[]) {
    this.resource.code = codings.map((coding) =>
      codeableConcept(coding as CodingValue)
    )
  }

  get roleCodes(): string[] {
    return this.roles.map((r) => r.code ?? '')
  }

  get idtRole(): Coding | undefined {
    return this.roles.find((coding) => coding.system === PRACTITIONER_ROLE)
  }

  hasRole(role: CodeableConcept): boolean {
    return hasCodeableConcept(this.resource.code, role)
  }

  addRole(coding: Coding): void {
    this.roles = mergeSystemValues(this.roles, coding)
  }

  removeRole(roleCode: string, system?: string): void {
    this.resource.code = this.resource.code?.filter((code) => {
      const coding = getCodingFromCodeableConcept(code)
      if (!coding) {
        return true
      }
      return !(!system || coding.system === system) || coding.code !== roleCode
    })
  }

  get active(): boolean | undefined {
    return this.resource.active
  }

  get idtRoleCode(): string | undefined {
    return this.idtRole?.code
  }

  get periodStart(): string | undefined {
    return this.resource.period?.start
  }

  get periodEnd(): string | undefined {
    return this.resource.period?.end
  }

  get specialty(): CodeableConcept[] {
    return this.resource.specialty ?? []
  }

  set specialty(specialty: CodeableConcept[]) {
    this.resource.specialty = specialty
  }

  get specialtyCodes(): string[] | undefined {
    return this.resource.specialty?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get location(): Reference<Location>[] {
    return (this.resource.location ?? []) as Reference<Location>[]
  }

  set location(location: Reference<Location>[]) {
    this.resource.location = location
  }

  // First linked location — the office matching the practitioner's primary
  // address. Used to default the appointment place-of-service.
  get primaryLocation(): Reference<Location> | undefined {
    return this.location[0]
  }

  get locationIds(): string[] | undefined {
    return this.resource.location?.map((l) => resolveId(l) ?? '')
  }

  get telecomDetails(): string[] | undefined {
    return this.resource.telecom?.map((t) => `${t.system}: ${t.value}`)
  }

  get organization(): Reference<Organization> | undefined {
    return this.resource.organization as Reference<Organization> | undefined
  }

  set organization(org: Reference<Organization> | undefined) {
    this.resource.organization = org
  }

  protected getExportFields() {
    return {
      id: this.id,
      active: this.active,
      practitionerId: resolveId(this.practitioner),
      practitionerDisplay: this.practitioner.display,
      roles: this.roleCodes,
      idtRole: this.idtRoleCode,
      start: this.periodStart,
      end: this.periodEnd,
      specialty: this.specialtyCodes,
      locationIds: this.locationIds,
      telecom: this.telecomDetails,
    }
  }
}
